import { useEffect, useState, useContext, useCallback } from "react";
import { Grid } from "@mui/material";
import styles from "./styles.module.css";
import Loader from "../../utils/Loading";
import { KEYBOARD_KEYS, EVENTS } from "const";
import { eventsEmitter } from "utils/events";
import { useNavigate } from "react-router-dom";
import TerminalForceAttachDialog from "../../popups/TerminalForceAttachDialog";
import SalesPersonContext from "context/SalesPersonContext";
import * as TerminalHelper from "helpers/TermialHelper";
import NotificationContext from "context/NotificationContext";
import { navigateToNextPosStep } from "helpers/NavigationHelper";

function TerminalSelection() {
  const navigate = useNavigate();

  const { loadSalesPersons } = useContext(SalesPersonContext);
  const { reCreateAndStoreNotificationToken } = useContext(NotificationContext);

  const [isLoading, setIsLoading] = useState(false);
  const [terminals, setTerminals] = useState([]);
  const [forceAttachTerminal, setForceAttachTerminal] = useState(null);
  const [focusedIndex, setFocusedIndex] = useState(0);

  const loadTerminals = async () => {
    setIsLoading(true);
    try {
      const resp = await TerminalHelper.getPosTerminals();
      setTerminals((resp || []).slice().sort((a, b) => a.id - b.id));
      setFocusedIndex(0);
    } catch (error) {
      console.log("Error: ", error);
      setTerminals([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAttachTerminal = useCallback(
    async (terminal, force = false) => {
      if (terminal?.mac_id && force !== true) {
        setForceAttachTerminal(terminal);
        return;
      }
      setForceAttachTerminal(null);
      setIsLoading(true);

      try {
        await TerminalHelper.attachTerminal(terminal, force);

        // Generate and save FCM token, Skipping await to make it non-blocking
        reCreateAndStoreNotificationToken();

        await loadSalesPersons();
        await navigateToNextPosStep(navigate);
      } catch (error) {
        console.log("Attach terminal failed:", error);
      } finally {
        setIsLoading(false);
      }
    },
    [loadSalesPersons, navigate, reCreateAndStoreNotificationToken],
  );

  useEffect(() => {
    if (TerminalHelper.getCurrentTerminalId()) {
      navigateToNextPosStep(navigate, { replace: true });
      return;
    }
    loadTerminals();
  }, [navigate]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (terminals.length === 0) return;

      if (e.key === KEYBOARD_KEYS.ARROW_DOWN || e.key === KEYBOARD_KEYS.ARROW_RIGHT) {
        setFocusedIndex((prev) => (prev + 1) % terminals.length);
      } else if (e.key === KEYBOARD_KEYS.ARROW_UP || e.key === KEYBOARD_KEYS.ARROW_LEFT) {
        setFocusedIndex((prev) => (prev - 1 + terminals.length) % terminals.length);
      } else if (e.key === KEYBOARD_KEYS.ENTER) {
        handleAttachTerminal(terminals[focusedIndex]);
      }
    };

    const unsubscribeFn = eventsEmitter.on(EVENTS.KEY_DOWN, handleKeyDown);
    return unsubscribeFn;
  }, [terminals, focusedIndex, handleAttachTerminal]);

  return (
    <>
      <Loader isOpen={isLoading} backdropSx={{ borderRadius: "16px" }} />
      <Grid container className={styles.textArea}>
        <Grid item xs={12}>
          <Grid container>
            <Grid item xs={12} display="flex" justifyContent={"center"}>
              <span className={styles.head1}>
                Select <span className={styles.head2}>Terminal</span>
              </span>
            </Grid>
            <Grid item xs={12} className={styles.text}>
              <span>
                Please select your preferred terminal from the list below to proceed and continue to the next step of
                the setup
              </span>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} style={{ paddingTop: "20px" }}>
          <div className={styles.selectWrapper}>
            {isLoading ? (
              <div className={styles.loadingMessage}>Loading terminals...</div>
            ) : (
              (terminals || []).map((terminal, index) => (
                <div
                  key={terminal?.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => {
                    setFocusedIndex(index);
                    handleAttachTerminal(terminal);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === KEYBOARD_KEYS.ENTER || e.key === " ") handleAttachTerminal(terminal);
                  }}
                  className={styles.selectContainer}
                  style={{
                    backgroundColor: index === focusedIndex ? "rgb(239 239 239)" : "white",
                  }}
                >
                  <div className={styles.itemLeft}>
                    <div className={styles.terminalName}>{terminal?.name}</div>
                    <div className={styles.statusText}>
                      {terminal?.mac_id ? (
                        <span className={styles.attached}>Attached</span>
                      ) : (
                        <span className={styles.canAttach}>Can attach</span>
                      )}
                    </div>
                  </div>

                  <div className={styles.itemRight}>
                    <div className={styles.location}>{terminal?.location}</div>
                    <div className={styles.macId}>{terminal?.mac_id || ""}</div>
                  </div>
                </div>
              ))
            )}
          </div>
        </Grid>
      </Grid>
      {forceAttachTerminal && (
        <TerminalForceAttachDialog
          handleClose={() => setForceAttachTerminal(null)}
          forceAttachTerminal={() => handleAttachTerminal(forceAttachTerminal, true)}
        />
      )}
    </>
  );
}

export default TerminalSelection;
